import { CartHelper } from "./CartHelper.js"
import { I18N } from "./I18N.js"
import { Modal } from "./lib/Modal.js"
import { Toast } from "./Toast.js"

export class Cart {
	_target
	_appContext
	_onOrderUpdatedHandlers = []
	_store
	_cartVO
	_cartPark = []
	_hideTax = true
	_hideNet = true
	_visible = false


	constructor(target) {
		this._target = document.getElementById(target)
		this._target.setAttribute('hidden', '')
		this._target.innerHTML = `
			<h2 i18n>Cart</h2>
			<div><span i18n>Store</span>: <span id="cartStore"></span></div>
			<div id="cartItems"></div>
		`
		this.empty()
		this._calculateCartPrice()
		this._displayCartItems()
		I18N.translateElement(this._target)
	}

	addOnCartUpdatedHandlers = (...onOrderUpdatedHandlers) => this._onOrderUpdatedHandlers.push(...onOrderUpdatedHandlers)

	addItemVO = (itemVO, store) => {
		if (this._store && this._store.id !== store.id && this._cartVO.items.length) {
			// TODO Use dialog
			const dialog = new Modal(Modal.DECISION)
			dialog.setContentInnerHTML(I18N.translateAndFormat('Do you want to buy {0} and remove all previous items from your cart at {1}?', itemVO.product.productName, this._store.name))
			dialog.setButtonGroupInnerHTML('<button class="primary" i18n>yes</button><button class="primary" i18n>no</button>')
			dialog.setBtnClickHandler(0, () => {
				this._store = store
				document.getElementById('cartStore').innerHTML = this._store.name
				// Empty cart when store changes
				this.empty()
				this._addItem(itemVO)
			})
			dialog.setBtnClickHandler(1, () => {
				new Toast('toaster', I18N.translate('No item added to cart!'), Toast.WARN, 7)
			})
		}
		else {
			this._store = store
			document.getElementById('cartStore').innerHTML = this._store.name
			this._addItem(itemVO)
		}
	}

	setStore = store => {
		// Warn about store change
		if (this._store && this._store.id !== store.id && this._cartVO.items.length) {
			new Toast('toaster', I18N.translateAndFormat('Your shopping cart already contains items from {0}. These must be removed in order to purchase items from {1}.', this._store.name, store.name), Toast.INFO, 7)
		}
		if (!this._store) {
			this._store = store
			document.getElementById('cartStore').innerHTML = this._store.name
		}
	}

	setAppContext = appContext => this._appContext = appContext

	setVisible = visible => {
		if (visible) {
			if (this._cartVO.items && this._cartVO.items.length) {
				this._target.removeAttribute('hidden')
			}
		}
		else {
			this._target.setAttribute('hidden', '')
		}
		this._visible = visible
	}

	empty = () => {
		if (this._cartVO && this._cartVO.items.length) {
			this._cartPark.push(JSON.parse(JSON.stringify(this._cartVO)))
		}
		this._cartVO = {
			cartId: CartHelper.getUuid(),
			items: []
		}
		this._calculateCartPrice()
		this._displayCartItems()
		this._onOrderUpdatedHandlers.forEach(handler => handler(this._cartVO))
		if (this._appContext) {
			this._appContext.setOpen('cart', false)
			this._target.setAttribute('hidden', '')
		}
	}

	restore = () => {
		if (this._cartPark.length) {
			this._cartVO = this._cartPark.pop()
			this._calculateCartPrice()
			this._displayCartItems()
			this._onOrderUpdatedHandlers.forEach(handler => handler(this._cartVO))
			if (this._appContext) {
				this._appContext.setOpen('cart', false)
				this._target.setAttribute('hidden', '')
				this._appContext.setActive('products')
			}
		}
		else {
			console.warn('No cart to restore!')
		}
	}

	_addItem = itemVO => {
		const existingItem = this._cartVO.items.find(i => i.product.productId === itemVO.product.productId
			&& JSON.stringify(i.extraCategories) === JSON.stringify(itemVO.extraCategories)
			&& i.remarks === itemVO.remarks
			&& i.variant === itemVO.variant)
		if (existingItem) {
			existingItem.qty = Number(existingItem.qty) + itemVO.qty
		}
		else {
			const newPos = this._cartVO.items.length + 1
			itemVO.pos = newPos
			this._cartVO.items.push(itemVO)
		}
		this._calculateCartPrice()
		this._displayCartItems()
		I18N.translateElement(document.getElementById('cartItems'))
		this._onOrderUpdatedHandlers.forEach(handler => handler(this._cartVO))
		if (this._getItemCnt(this._cartVO) === 0) {
			this._appContext.setOpen('cart', false)
			this._target.setAttribute('hidden', '')
		}
		else {
			this._appContext.setOpen('cart', true)
			if (this._visible) {
				this._target.removeAttribute('hidden')
			}
		}
		console.debug('Item added to cart: ', itemVO)
		new Toast('toaster', I18N.translate('Item added to cart'), Toast.INFO, 2)
	}

	_calculateCartPrice = () => {
		this._cartVO.net = CartHelper.calculateCartNet(this._cartVO)
		this._cartVO.tax = CartHelper.calculateCartTax(this._cartVO)
	}

	_displayCartItems = () => {
		const getOptions = (category, qty) => category.extraOptions.map(o => `<tr>
				<td>&nbsp;* ${o.extraOptionName}</td>
				${this._hideNet ? '' : `<td class="priceColumn">${o.net !== 0 ? CartHelper.formatPrice(qty * o.net) : ''}</td>`}
				${this._hideTax ? '' : `<td class="priceColumn">${o.tax !== 0 ? CartHelper.formatPrice(qty * o.tax) : ''}</td>`}
				<td class="priceColumn">${o.net !== 0 || o.tax !== 0 ? CartHelper.formatPrice(qty * (o.net + o.tax)) : ''}</td>
				<td></td>
			</tr>`).join('')
		const getOptionCats = item => item.extraCategories.map(c => `${getOptions(c, item.qty)}`).join('')
		const getRemarks = item => item.remarks && item.remarks.length > 0 ? `<tr>
				<td colspan="4" class="remarksColumn">
					<div>
						<div>&nbsp;*&nbsp;<span i18n>Rem.</span>:&nbsp;</div>
						<div><i>${item.remarks}</i></div>
					</div>
				</td>
				<td></td>
			</tr>` : ''
		const getItems = () => this._cartVO.items.map(i => `<tr>
				<td><strong>${i.qty} x ${i.product.productName}${i.variant ? ` - ${i.variant.name}` : ''}</strong></td>
				${this._hideNet ? '' : `<td class="priceColumn">${CartHelper.formatPrice(CartHelper.calculateProductNet(i))}</td>`}
				${this._hideTax ? '' : `<td class="priceColumn">${CartHelper.formatPrice(CartHelper.calculateProductTax(i))}</td>`}
				<td class="priceColumn">${CartHelper.formatPrice(CartHelper.calculateProductNet(i) + CartHelper.calculateProductTax(i))}</td>
				<td><button id="cartRemove${i.pos}Btn" title="${I18N.translate('Remove ')}" i18n>Remove</button></td>
			</tr>
			${getOptionCats(i)}
			${getRemarks(i)}`).join('')

		const table = `<table class="priceTable">
			<thead>
				<td></td>
				${this._hideNet ? '' : `<td class="priceColumn" i18n>Net</td>`}
				${this._hideTax ? '' : `<td class="priceColumn" i18n>Tax</td>`}
				${this._hideNet && this._hideTax ? '' : `<td class="priceColumn" i18n>Gross</td>`}
				<td></td>
			</thead>
			<tbody>
				${getItems()}
				<tr class="sumRow">
					<td class="sumRow"i18n>Items Value</td>
					${this._hideNet ? '' : `<td class="priceColumn">${CartHelper.formatPrice(this._cartVO.net)}</td>`}
					${this._hideTax ? '' : `<td class="priceColumn">${CartHelper.formatPrice(this._cartVO.tax)}</td>`}
					<td class="priceColumn">${CartHelper.formatPrice(this._cartVO.net + this._cartVO.tax)}</td>
					<td></td>
				</tr>
			</tbody>
		</table>`
		document.getElementById('cartItems').innerHTML = table
		// Add remove handlers
		this._cartVO.items.forEach(i => document.getElementById(`cartRemove${i.pos}Btn`).addEventListener('click', () => this._removeItem(i.pos)))
	}

	_removeItem = pos => {
		this._cartVO.items = this._cartVO.items.filter(i => i.pos !== pos)
		this._calculateCartPrice()
		this._displayCartItems()
		I18N.translateElement(document.getElementById('cartItems'))
		this._onOrderUpdatedHandlers.forEach(handler => handler(this._cartVO))
		if (this._getItemCnt(this._cartVO) === 0) {
			this._appContext.setOpen('cart', false)
			this._target.setAttribute('hidden', '')
			this._appContext.setActive('products')
		}
		else {
			this._appContext.setOpen('cart', true)
			this._target.removeAttribute('hidden')
		}
	}

	_getItemCnt = cartVO => {
		let nr = 0
		if (cartVO && cartVO.items && cartVO.items.length) {
			cartVO.items.forEach(item => {
				nr += item.qty
			})
		}
		return nr
	}

}